import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Snackbar } from '@mui/material'
import { useCall } from '../hooks/useCall'
import {
  CallActionButton,
  CallBanner,
  CallBannerActions,
  CallBannerInfo,
  CallCountPill,
  CallDock,
  CallWaitingCard,
  InCallActions,
  InCallBar,
  InCallInfo,
  InCallName,
  InCallStatus,
} from '../styles'

const CALL_ERROR_AUTO_HIDE_MS = 6000

function formatDuration(connectedAt: number, now: number): string {
  const totalSeconds = Math.max(0, Math.floor((now - connectedAt) / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function CallPanel() {
  const { t } = useTranslation()
  const {
    incomingCall,
    otherRingingCount,
    waitingCalls,
    phase,
    activeCall,
    connectedAt,
    isMuted,
    error,
    remoteAudioRef,
    answer,
    reject,
    hangup,
    toggleMute,
    dismissError,
  } = useCall()

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (phase !== 'connected') return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [phase])

  const firstWaitingCall = waitingCalls[0]

  return (
    <>
      <audio ref={remoteAudioRef} autoPlay />

      {incomingCall && (
        <CallBanner role="alert">
          <CallBannerInfo>
            📞 {t('calls.incoming_from', {
              name: incomingCall.contactName || incomingCall.contactPhone,
            })}
            {otherRingingCount > 0 && (
              <CallCountPill>
                {t('calls.more_ringing', { count: otherRingingCount })}
              </CallCountPill>
            )}
          </CallBannerInfo>
          <CallBannerActions>
            <CallActionButton
              variant="answer"
              onClick={() => void answer(incomingCall.callId)}
            >
              {t('calls.answer')}
            </CallActionButton>
            <CallActionButton
              variant="reject"
              onClick={() => reject(incomingCall.callId)}
            >
              {t('calls.reject')}
            </CallActionButton>
          </CallBannerActions>
        </CallBanner>
      )}

      {activeCall && phase !== 'idle' && (
        <CallDock>
          {firstWaitingCall && (
            <CallWaitingCard role="status" aria-live="polite">
              <span className="dot" />
              <span className="name">
                {t('calls.waiting', {
                  name: firstWaitingCall.contactName || firstWaitingCall.contactPhone,
                })}
              </span>
              {waitingCalls.length > 1 && (
                <span className="count">
                  {t('calls.waiting_more', { count: waitingCalls.length - 1 })}
                </span>
              )}
            </CallWaitingCard>
          )}

          <InCallBar>
            <InCallInfo>
              <InCallName>
                {activeCall.contactName || activeCall.contactPhone}
              </InCallName>
              <InCallStatus>
                {phase === 'answering' && t('calls.answering')}
                {phase === 'connecting' && t('calls.connecting')}
                {phase === 'connected' &&
                  connectedAt !== null &&
                  formatDuration(connectedAt, now)}
              </InCallStatus>
            </InCallInfo>
            <InCallActions>
              <CallActionButton
                variant="mute"
                onClick={toggleMute}
                disabled={phase === 'answering'}
                aria-label={isMuted ? t('calls.unmute') : t('calls.mute')}
                title={isMuted ? t('calls.unmute') : t('calls.mute')}
              >
                {isMuted ? '🔇' : '🎙️'}
              </CallActionButton>
              <CallActionButton
                variant="hangup"
                onClick={hangup}
                disabled={phase === 'answering'}
              >
                {t('calls.hangup')}
              </CallActionButton>
            </InCallActions>
          </InCallBar>
        </CallDock>
      )}

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={CALL_ERROR_AUTO_HIDE_MS}
        onClose={dismissError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {error ? (
          <Alert severity="error" variant="filled" onClose={dismissError}>
            {t(`calls.errors.${error}`)}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  )
}
